import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Link,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';

interface Column {
  key: string;
  label: string;
  type: string;
  sortable?: boolean;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  linkTo?: string; // For link type columns
}

interface Features {
  pagination?: boolean;
  sorting?: boolean;
  selection?: boolean;
  striped?: boolean;
  hover?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
}

interface CustomAction {
  label: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  onClick: (row: any) => void;
  disabled?: (row: any) => boolean;
  tooltip?: (row: any) => string;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  features?: Features;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onBulkDelete?: (selectedRows: any[]) => void;
  onBulkPrint?: (selectedRows: any[]) => void;
  customActions?: CustomAction[];
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  loading = false,
  features = {},
  onEdit,
  onDelete,
  onBulkDelete,
  onBulkPrint,
  customActions = [],
}) => {
  const navigate = useNavigate();
  const hasActions = onEdit || onDelete || customActions.length > 0;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(features.pageSize || 25);
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<number[]>([]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (columnKey: string) => {
    const isAsc = orderBy === columnKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnKey);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((_, index) => index);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelectRow = (index: number) => {
    const selectedIndex = selected.indexOf(index);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const formatCellValue = (value: any, column: Column, row: any) => {
    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'checkbox':
        return <Checkbox checked={Boolean(value)} disabled />;
      case 'number':
        return value.toLocaleString();
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'link':
        if (column.linkTo) {
          // Replace placeholders in linkTo with actual row values
          const link = column.linkTo.replace(/{(\w+)}/g, (_, key) => row[key]);
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(link)}
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              {String(value)}
            </Link>
          );
        }
        return String(value);
      default:
        return String(value);
    }
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, orderBy, order]);

  const paginatedData = features.pagination
    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedData;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Bulk Actions Toolbar */}
      {features.selection && selected.length > 0 && (onBulkDelete || onBulkPrint) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="subtitle1">
            {selected.length} item{selected.length > 1 ? 's' : ''} selected
          </Typography>
          {onBulkPrint && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => {
                const selectedRows = selected.map(index => data[index]);
                onBulkPrint(selectedRows);
              }}
            >
              Print Selected
            </Button>
          )}
          {onBulkDelete && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                const selectedRows = selected.map(index => data[index]);
                onBulkDelete(selectedRows);
              }}
              sx={{ ml: 1 }}
            >
              Delete Selected
            </Button>
          )}
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {features.selection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < data.length}
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  style={{ width: column.width }}
                  sortDirection={orderBy === column.key ? order : false}
                >
                  {column.sortable && features.sorting ? (
                    <TableSortLabel
                      active={orderBy === column.key}
                      direction={orderBy === column.key ? order : 'asc'}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell align="center" style={{ width: 120 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, rowIndex) => {
              const isSelected = selected.indexOf(rowIndex) !== -1;

              return (
                <TableRow
                  key={rowIndex}
                  hover={features.hover}
                  selected={isSelected}
                  sx={features.striped && rowIndex % 2 === 1 ? { backgroundColor: 'action.hover' } : {}}
                >
                  {features.selection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectRow(rowIndex)}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align || 'left'}>
                      {formatCellValue(row[column.key], column, row)}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center" alignItems="center">
                        {customActions.map((action, idx) => {
                          const isDisabled = action.disabled ? action.disabled(row) : false;
                          const tooltipText = action.tooltip ? action.tooltip(row) : action.label;

                          return (
                            <Tooltip key={idx} title={tooltipText}>
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color={action.color || 'primary'}
                                  onClick={() => action.onClick(row)}
                                  disabled={isDisabled}
                                  startIcon={action.icon}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  {action.label}
                                </Button>
                              </span>
                            </Tooltip>
                          );
                        })}
                        {onEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {features.pagination && (
        <TablePagination
          rowsPerPageOptions={features.pageSizeOptions || [10, 25, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default DataTable;
